/* eslint-disable react/no-array-index-key */
/* eslint-disable no-nested-ternary */
/* eslint-disable react/prop-types */
/* eslint-disable no-unused-vars */
import React, { useCallback, useEffect, useRef, useState } from "react";
import MDButton from "components/MDButton";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import DashboardLayout from "pages/LayoutContainers/DashboardLayout";
import DashboardNavbar from "pages/Navbars/DashboardNavbar";
import MDTypography from "components/MDTypography";
import useFetchWithMsal from "hooks/useFetchWithMsal";
import { protectedResources } from "authConfig";
import { useCurrentUser } from "context/UserContext";

const API_BASE = protectedResources.apiTodoList.endpoint;

const SOURCE_TYPES = [
  // "AI Web Search" (llm_search) is intentionally hidden for now — unreliable results.
  // The data-source code remains so existing entries still work and it can be re-enabled later.
  { value: "stock_yahoo", label: "Stock Tracker (Yahoo Finance)" },
  { value: "fx_alphavantage", label: "FX Tracker (Alpha Vantage)" },
  { value: "url_scrape", label: "URL Scrape" },
  { value: "rss_feed", label: "RSS / Atom Feed" },
  { value: "excel_online", label: "Online Excel / CSV" },
  { value: "econdb", label: "EconDB Series" },
  { value: "custom_api", label: "Custom API" },
];

const FREQUENCIES = [
  { value: "hourly", label: "Hourly" },
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
];

const EXTRACTION_TYPES = [
  { value: "json_path", label: "JSON Path" },
  { value: "css_selector", label: "CSS Selector" },
  { value: "regex", label: "Regex" },
];

const TYPE_LABELS = {
  llm_search: "AI Web Search",
  stock_yahoo: "Stock (Yahoo)",
  fx_alphavantage: "FX (AlphaVantage)",
  url_scrape: "URL Scrape",
  rss_feed: "RSS / Atom Feed",
  excel_online: "Online Excel/CSV",
  econdb: "EconDB",
  custom_api: "Custom API",
};

const EMPTY_FORM = {
  name: "",
  description: "",
  dataSourceType: "custom_api",
  unit: "",
  provider: "",
  updateFrequency: "daily",
  regions: [], // [{ name, queryOverride }]
  // llm_search
  llmQuery: "",
  llmValueHint: "",
  llmHistoricalDays: "30",
  // stock_yahoo
  stockCode: "",
  // fx_alphavantage
  fromCurrency: "",
  toCurrency: "",
  // url_scrape
  scrapeUrl: "",
  extractionType: "json_path",
  valueConfig: "",
  dateConfig: "",
  // rss_feed
  rssUrl: "",
  rssValueElement: "description",
  rssValueRegex: "",
  rssItemIndex: "0",
  rssDateElement: "pubDate",
  // excel_online
  excelUrl: "",
  dateCol: "",
  valueCol: "",
  filterCol: "",
  filterValue: "",
  // econdb
  seriesCode: "",
  // custom_api
  apiUrl: "",
  apiHeaders: "",
  valueJsonPath: "",
  dateJsonPath: "",
  queryParams: "",
  historicalCount: "0",
  historicalLengthParam: "length",
};

function buildConfig(form) {
  switch (form.dataSourceType) {
    case "llm_search":
      return {
        query: form.llmQuery,
        valueHint: form.llmValueHint,
        historicalDays: parseInt(form.llmHistoricalDays, 10) || 30,
      };
    case "stock_yahoo":
      return { stockCode: form.stockCode };
    case "fx_alphavantage":
      return { fromCurrency: form.fromCurrency, toCurrency: form.toCurrency };
    case "url_scrape":
      return {
        url: form.scrapeUrl,
        extractionType: form.extractionType,
        valueConfig: form.valueConfig,
        dateConfig: form.dateConfig,
      };
    case "rss_feed":
      return {
        url: form.rssUrl,
        valueElement: form.rssValueElement || "description",
        valueRegex: form.rssValueRegex || "",
        itemIndex: parseInt(form.rssItemIndex, 10) || 0,
        dateElement: form.rssDateElement || "pubDate",
      };
    case "excel_online":
      return {
        url: form.excelUrl,
        dateCol: form.dateCol,
        valueCol: form.valueCol,
        filterCol: form.filterCol,
        filterValue: form.filterValue,
      };
    case "econdb":
      return { seriesCode: form.seriesCode };
    case "custom_api":
      return {
        url: form.apiUrl,
        headers: form.apiHeaders ? JSON.parse(form.apiHeaders) : {},
        valueJsonPath: form.valueJsonPath,
        dateJsonPath: form.dateJsonPath,
        queryParams: form.queryParams ? JSON.parse(form.queryParams) : {},
        historicalCount: parseInt(form.historicalCount, 10) || 0,
        historicalLengthParam: form.historicalLengthParam.trim() || "length",
      };
    default:
      return {};
  }
}

function formFromDoc(doc) {
  const c = doc.dataSourceConfig || {};
  return {
    name: doc.name || "",
    description: doc.description || "",
    dataSourceType: doc.dataSourceType || "stock_yahoo",
    unit: doc.unit || "",
    provider: doc.provider || "",
    updateFrequency: doc.updateFrequency || "daily",
    stockCode: c.stockCode || "",
    fromCurrency: c.fromCurrency || "",
    toCurrency: c.toCurrency || "",
    scrapeUrl: c.url || "",
    extractionType: c.extractionType || "json_path",
    valueConfig: c.valueConfig || "",
    dateConfig: c.dateConfig || "",
    rssUrl: c.url || "",
    rssValueElement: c.valueElement || "description",
    rssValueRegex: c.valueRegex || "",
    rssItemIndex: String(c.itemIndex ?? 0),
    rssDateElement: c.dateElement || "pubDate",
    excelUrl: c.url || "",
    dateCol: c.dateCol || "",
    valueCol: c.valueCol || "",
    filterCol: c.filterCol || "",
    filterValue: c.filterValue || "",
    llmQuery: c.query || "",
    llmValueHint: c.valueHint || "",
    llmHistoricalDays: String(c.historicalDays || 30),
    seriesCode: c.seriesCode || "",
    apiUrl: c.url || "",
    apiHeaders: c.headers ? JSON.stringify(c.headers, null, 2) : "",
    valueJsonPath: c.valueJsonPath || "",
    dateJsonPath: c.dateJsonPath || "",
    queryParams: c.queryParams ? JSON.stringify(c.queryParams, null, 2) : "",
    historicalCount: String(c.historicalCount || 0),
    historicalLengthParam: c.historicalLengthParam || "length",
    regions: Array.isArray(doc.regions)
      ? doc.regions.map((r) => ({ name: r.name || "", queryOverride: r.queryOverride || "" }))
      : [],
  };
}

function ConfigFields({ form, onChange }) {
  const f = (field) => (e) => onChange({ ...form, [field]: e.target.value });

  switch (form.dataSourceType) {
    case "stock_yahoo":
      return (
        <TextField
          fullWidth
          size="small"
          label="Stock Code"
          placeholder="e.g. QAN.AX, AAPL"
          value={form.stockCode}
          onChange={f("stockCode")}
          required
        />
      );
    case "fx_alphavantage":
      return (
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <TextField
              fullWidth
              size="small"
              label="From Currency"
              placeholder="e.g. AUD"
              value={form.fromCurrency}
              onChange={f("fromCurrency")}
              required
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              size="small"
              label="To Currency"
              placeholder="e.g. USD"
              value={form.toCurrency}
              onChange={f("toCurrency")}
              required
            />
          </Grid>
        </Grid>
      );
    case "url_scrape":
      return (
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="URL to scrape"
              value={form.scrapeUrl}
              onChange={f("scrapeUrl")}
              required
            />
          </Grid>
          <Grid item xs={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Extraction type</InputLabel>
              <Select
                value={form.extractionType}
                label="Extraction type"
                onChange={f("extractionType")}
              >
                {EXTRACTION_TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={4}>
            <TextField
              fullWidth
              size="small"
              label="Value selector / path"
              placeholder="e.g. $.data.value"
              value={form.valueConfig}
              onChange={f("valueConfig")}
              required
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              fullWidth
              size="small"
              label="Date selector / path"
              placeholder="e.g. $.data.date"
              value={form.dateConfig}
              onChange={f("dateConfig")}
            />
          </Grid>
        </Grid>
      );
    case "rss_feed":
      return (
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Feed URL"
              placeholder="e.g. https://oilcrisistracker.com/feed.rss"
              value={form.rssUrl}
              onChange={f("rssUrl")}
              required
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              fullWidth
              size="small"
              label="Value element"
              placeholder="e.g. title, description, price"
              helperText="Element name inside each item/entry"
              value={form.rssValueElement}
              onChange={f("rssValueElement")}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              fullWidth
              size="small"
              label="Value regex"
              placeholder={String.raw`e.g. Brent:\s*([\d.]+)`}
              helperText="Regex to extract the number (capture group 1 or full match)"
              value={form.rssValueRegex}
              onChange={f("rssValueRegex")}
            />
          </Grid>
          <Grid item xs={2}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Item index"
              helperText="0 = first/latest"
              value={form.rssItemIndex}
              onChange={f("rssItemIndex")}
              inputProps={{ min: 0 }}
            />
          </Grid>
          <Grid item xs={2}>
            <TextField
              fullWidth
              size="small"
              label="Date element"
              placeholder="pubDate"
              helperText="e.g. pubDate, updated"
              value={form.rssDateElement}
              onChange={f("rssDateElement")}
            />
          </Grid>
        </Grid>
      );
    case "excel_online":
      return (
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="File URL (Excel or CSV)"
              value={form.excelUrl}
              onChange={f("excelUrl")}
              required
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              fullWidth
              size="small"
              label="Date column"
              placeholder="Date"
              value={form.dateCol}
              onChange={f("dateCol")}
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              fullWidth
              size="small"
              label="Value column"
              placeholder="Price"
              value={form.valueCol}
              onChange={f("valueCol")}
              required
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              fullWidth
              size="small"
              label="Filter column"
              placeholder="Type"
              value={form.filterCol}
              onChange={f("filterCol")}
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              fullWidth
              size="small"
              label="Filter value"
              placeholder="Close"
              value={form.filterValue}
              onChange={f("filterValue")}
            />
          </Grid>
        </Grid>
      );
    case "econdb":
      return (
        <TextField
          fullWidth
          size="small"
          label="EconDB Series Code"
          placeholder="e.g. RSTCNTAUSM683SNBR"
          value={form.seriesCode}
          onChange={f("seriesCode")}
          required
        />
      );
    case "custom_api":
      return (
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="API URL"
              value={form.apiUrl}
              onChange={f("apiUrl")}
              required
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              size="small"
              label="Value JSON path"
              placeholder="$.value"
              value={form.valueJsonPath}
              onChange={f("valueJsonPath")}
              required
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              size="small"
              label="Date JSON path"
              placeholder="$.date"
              value={form.dateJsonPath}
              onChange={f("dateJsonPath")}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              size="small"
              label="Headers (JSON)"
              placeholder='{"Authorization":"Bearer ..."}'
              multiline
              minRows={2}
              value={form.apiHeaders}
              onChange={f("apiHeaders")}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              size="small"
              label="Query params (JSON)"
              placeholder='{"limit":"100"}'
              multiline
              minRows={2}
              value={form.queryParams}
              onChange={f("queryParams")}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              fullWidth
              size="small"
              label="Bootstrap history count"
              type="number"
              inputProps={{ min: 0, max: 500 }}
              value={form.historicalCount}
              onChange={f("historicalCount")}
              helperText="Records to fetch on first run (0 = skip). E.g. 52 for a year of weekly data."
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              fullWidth
              size="small"
              label="History length param"
              placeholder="length"
              value={form.historicalLengthParam}
              onChange={f("historicalLengthParam")}
              helperText='Query param the API uses for record count (default "length").'
            />
          </Grid>
        </Grid>
      );
    case "llm_search":
      return (
        <Grid container spacing={1.5}>
          <Grid item xs={12}>
            <Box
              sx={{
                p: 1.5,
                bgcolor: "rgba(82,0,155,0.04)",
                borderRadius: 1,
                border: "1px solid rgba(82,0,155,0.15)",
                mb: 0.5,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                The platform searches the web daily using your query and uses AI to extract the
                numeric value automatically. On first run it also retrieves historical data to seed
                the chart.
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Search query"
              placeholder='e.g. "current jet fuel price per barrel USD" or "AUD USD exchange rate today"'
              value={form.llmQuery}
              onChange={f("llmQuery")}
              required
              helperText="Natural language — describe what you want to track. Be specific about the indicator, unit and currency."
              multiline
              minRows={2}
            />
          </Grid>
          <Grid item xs={12} sm={8}>
            <TextField
              fullWidth
              size="small"
              label="Extraction hint (optional)"
              placeholder='e.g. "price in USD per barrel as a plain number, not index value"'
              value={form.llmValueHint}
              onChange={f("llmValueHint")}
              helperText="Helps the AI pick the right number when results contain multiple figures."
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="Historical days (first run)"
              type="number"
              inputProps={{ min: 7, max: 365 }}
              value={form.llmHistoricalDays}
              onChange={f("llmHistoricalDays")}
              helperText="Days of history to seed the chart on first run (7–365)."
            />
          </Grid>
        </Grid>
      );
    default:
      return null;
  }
}

export default function SignpostLibrary() {
  const { execute } = useFetchWithMsal({ scopes: [protectedResources.apiTodoList.scopes?.[0]] });
  const executeRef = useRef(execute);
  executeRef.current = execute;
  const { role } = useCurrentUser();
  const isAdmin = ["RISK_ADMIN", "SUPER_RISK_ADMIN"].includes((role || "").toUpperCase());

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [snack, setSnack] = useState({ open: false, msg: "", severity: "success" });

  const toast = (msg, severity = "success") => setSnack({ open: true, msg, severity });

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const res = await executeRef.current("GET", `${API_BASE}/signpostLibrary/list`);
      setEntries(Array.isArray(res?.data) ? res.data : []);
    } catch {
      toast("Failed to load signpost library.", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const openCreate = () => {
    setEditId(null);
    setForm(EMPTY_FORM);
    setTestResult(null);
    setDialogOpen(true);
  };
  const openEdit = (doc) => {
    setEditId(doc.id);
    setForm(formFromDoc(doc));
    setTestResult(null);
    setDialogOpen(true);
  };
  const closeDialog = () => {
    setDialogOpen(false);
    setEditId(null);
    setTestResult(null);
  };

  const handleTest = async () => {
    let config;
    try {
      config = buildConfig(form);
    } catch {
      toast("Invalid JSON in headers or query params.", "warning");
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const res = await executeRef.current("POST", `${API_BASE}/signpostLibrary/test`, {
        dataSourceType: form.dataSourceType,
        dataSourceConfig: config,
      });
      setTestResult(res?.data || { success: false, error: "No response data." });
    } catch {
      setTestResult({ success: false, error: "Request failed — check API URL and config." });
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast("Name is required.", "warning");
      return;
    }
    let config;
    try {
      config = buildConfig(form);
    } catch {
      toast("Invalid JSON in headers or query params.", "warning");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim(),
        dataSourceType: form.dataSourceType,
        dataSourceConfig: config,
        unit: form.unit.trim(),
        provider: form.provider.trim(),
        updateFrequency: form.updateFrequency,
        regions: (form.regions || []).filter((r) => r.name.trim()),
      };
      if (editId) {
        await executeRef.current("PUT", `${API_BASE}/signpostLibrary/update/${editId}`, payload);
        toast("Library entry updated.");
      } else {
        await executeRef.current("POST", `${API_BASE}/signpostLibrary/create`, payload);
        toast("Library entry created.");
      }
      closeDialog();
      await fetchEntries();
    } catch {
      toast("Failed to save entry.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (doc) => {
    try {
      await executeRef.current("PATCH", `${API_BASE}/signpostLibrary/toggle/${doc.id}`, {});
      toast(`"${doc.name}" ${doc.isActive ? "deactivated" : "activated"}.`);
      await fetchEntries();
    } catch {
      toast("Failed to toggle entry.", "error");
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await executeRef.current("DELETE", `${API_BASE}/signpostLibrary/delete/${deleteTarget.id}`);
      toast(`"${deleteTarget.name}" deleted.`);
      setDeleteTarget(null);
      await fetchEntries();
    } catch {
      toast("Failed to delete entry.", "error");
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box sx={{ p: 2 }}>
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Box>
            <MDTypography variant="h5" fontWeight="bold">
              Signpost Library
            </MDTypography>
            <MDTypography variant="body2" color="text" sx={{ mt: 0.5 }}>
              Curated data-source configurations that tech users define here and risk owners pick
              from when adding signposts to an Emerging Risk record.
            </MDTypography>
          </Box>
          {isAdmin && (
            <MDButton
              variant="contained"
              backgroundColor="#52009b"
              color="#52009b"
              sx={{ color: "#f7f4fb" }}
              startIcon={<AddIcon />}
              onClick={openCreate}
            >
              Add to Library
            </MDButton>
          )}
        </Box>

        {/* Table */}
        <Card>
          <CardContent sx={{ p: 0 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f0f2f5" }}>
                    <TableCell>
                      <strong>Name</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Source Type</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Provider</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Unit</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Frequency</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Status</strong>
                    </TableCell>
                    {isAdmin && (
                      <TableCell align="center">
                        <strong>Actions</strong>
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4, color: "text.secondary" }}>
                        Loading…
                      </TableCell>
                    </TableRow>
                  ) : entries.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4, color: "text.secondary" }}>
                        {isAdmin
                          ? 'No entries yet. Click "Add to Library" to get started.'
                          : "No entries yet. Contact your RISK_ADMIN to add entries."}
                      </TableCell>
                    </TableRow>
                  ) : (
                    entries.map((doc) => (
                      <TableRow key={doc.id} hover sx={{ opacity: doc.isActive ? 1 : 0.55 }}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {doc.name}
                          </Typography>
                          {doc.description ? (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: "block" }}
                            >
                              {doc.description}
                            </Typography>
                          ) : null}
                          {Array.isArray(doc.regions) && doc.regions.length > 0 && (
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                              {doc.regions.map((r) => (
                                <Chip
                                  key={r.name}
                                  label={r.name}
                                  size="small"
                                  sx={{
                                    fontSize: 10,
                                    height: 18,
                                    bgcolor: "#ede7f6",
                                    color: "#52009b",
                                  }}
                                />
                              ))}
                            </Box>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={TYPE_LABELS[doc.dataSourceType] || doc.dataSourceType}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{doc.provider || "—"}</TableCell>
                        <TableCell>{doc.unit || "—"}</TableCell>
                        <TableCell sx={{ textTransform: "capitalize" }}>
                          {doc.updateFrequency}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={doc.isActive ? "Active" : "Inactive"}
                            size="small"
                            color={doc.isActive ? "success" : "default"}
                          />
                        </TableCell>
                        {isAdmin && (
                          <TableCell align="center">
                            <Tooltip title="Edit">
                              <IconButton size="small" onClick={() => openEdit(doc)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={doc.isActive ? "Deactivate" : "Activate"}>
                              <IconButton size="small" onClick={() => handleToggle(doc)}>
                                <PowerSettingsNewIcon
                                  fontSize="small"
                                  sx={{ color: doc.isActive ? "success.main" : "text.disabled" }}
                                />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton size="small" onClick={() => setDeleteTarget(doc)}>
                                <DeleteIcon fontSize="small" sx={{ color: "error.main" }} />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Create / Edit dialog */}
        <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="md" fullWidth>
          <DialogTitle>{editId ? "Edit Library Entry" : "Add Signpost to Library"}</DialogTitle>
          <DialogContent sx={{ pt: "16px !important" }}>
            <Grid container spacing={2}>
              {/* Row 1 — name + type */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Name *"
                  placeholder="e.g. Jet Fuel Price (WTI)"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Data Source Type *</InputLabel>
                  <Select
                    value={form.dataSourceType}
                    label="Data Source Type *"
                    onChange={(e) => setForm({ ...form, dataSourceType: e.target.value })}
                  >
                    {SOURCE_TYPES.map((t) => (
                      <MenuItem key={t.value} value={t.value}>
                        {t.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Row 2 — description */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Description"
                  multiline
                  minRows={2}
                  placeholder="Brief description of what this signpost measures and why it matters"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </Grid>

              {/* Row 3 — provider + unit + frequency */}
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Provider"
                  placeholder="e.g. Yahoo Finance"
                  value={form.provider}
                  onChange={(e) => setForm({ ...form, provider: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Unit"
                  placeholder="e.g. USD/barrel, %, index pts"
                  value={form.unit}
                  onChange={(e) => setForm({ ...form, unit: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Update Frequency</InputLabel>
                  <Select
                    value={form.updateFrequency}
                    label="Update Frequency"
                    onChange={(e) => setForm({ ...form, updateFrequency: e.target.value })}
                  >
                    {FREQUENCIES.map((f) => (
                      <MenuItem key={f.value} value={f.value}>
                        {f.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Row 4 — type-specific config */}
              <Grid item xs={12}>
                <Box sx={{ border: "1px solid #e0e0e0", borderRadius: 1, p: 1.5 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 1, display: "block" }}
                  >
                    Data source configuration
                  </Typography>
                  <ConfigFields
                    form={form}
                    onChange={(f) => {
                      setForm(f);
                      setTestResult(null);
                    }}
                  />
                </Box>
              </Grid>

              {/* Row 5 — Regional variants (llm_search only) */}
              {form.dataSourceType === "llm_search" && (
                <Grid item xs={12}>
                  <Box sx={{ border: "1px solid #e0e0e0", borderRadius: 1, p: 1.5 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Regional variants — one data series per region on the same chart
                      </Typography>
                      <MDButton
                        size="small"
                        variant="text"
                        sx={{ color: "#52009b", minWidth: 0, fontSize: 12 }}
                        startIcon={<AddIcon sx={{ fontSize: "14px !important" }} />}
                        onClick={() =>
                          setForm((prev) => ({
                            ...prev,
                            regions: [...(prev.regions || []), { name: "", queryOverride: "" }],
                          }))
                        }
                      >
                        Add Region
                      </MDButton>
                    </Box>
                    {(form.regions || []).length === 0 && (
                      <Typography variant="caption" color="text.disabled" sx={{ display: "block" }}>
                        No regions — this signpost will fetch a single global value.
                      </Typography>
                    )}
                    {(form.regions || []).map((reg, idx) => (
                      <Box
                        key={idx}
                        sx={{ display: "flex", gap: 1, alignItems: "flex-start", mb: 1 }}
                      >
                        <TextField
                          size="small"
                          label="Region name"
                          placeholder="e.g. Latin America"
                          value={reg.name}
                          onChange={(e) =>
                            setForm((prev) => {
                              const r = [...prev.regions];
                              r[idx] = { ...r[idx], name: e.target.value };
                              return { ...prev, regions: r };
                            })
                          }
                          sx={{ width: 180 }}
                        />
                        <TextField
                          size="small"
                          label="Query override"
                          placeholder="Leave blank to use base query"
                          value={reg.queryOverride}
                          onChange={(e) =>
                            setForm((prev) => {
                              const r = [...prev.regions];
                              r[idx] = { ...r[idx], queryOverride: e.target.value };
                              return { ...prev, regions: r };
                            })
                          }
                          sx={{ flex: 1 }}
                        />
                        <IconButton
                          size="small"
                          onClick={() =>
                            setForm((prev) => ({
                              ...prev,
                              regions: prev.regions.filter((_, i) => i !== idx),
                            }))
                          }
                          sx={{ color: "error.main", mt: 0.5 }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                </Grid>
              )}

              {/* Test result */}
              {testResult && (
                <Grid item xs={12}>
                  <Alert
                    severity={testResult.success ? "success" : "error"}
                    sx={{ fontSize: "0.8rem" }}
                  >
                    {testResult.success ? (
                      <>
                        {testResult.note && <div>{testResult.note}</div>}
                        {testResult.value !== null && testResult.value !== undefined && (
                          <div>
                            <strong>Value:</strong> {String(testResult.value)}
                            {testResult.date ? (
                              <span>
                                {" "}
                                &nbsp;|&nbsp; <strong>Date:</strong> {testResult.date}
                              </span>
                            ) : null}
                          </div>
                        )}
                        {testResult.rawPreview && (
                          <Box
                            component="pre"
                            sx={{
                              mt: 0.5,
                              fontSize: "0.7rem",
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-all",
                              maxHeight: 120,
                              overflowY: "auto",
                              bgcolor: "rgba(0,0,0,0.04)",
                              p: 0.75,
                              borderRadius: 1,
                            }}
                          >
                            {testResult.rawPreview}
                          </Box>
                        )}
                      </>
                    ) : (
                      testResult.error || "Test failed."
                    )}
                  </Alert>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions sx={{ justifyContent: "space-between" }}>
            <MDButton onClick={handleTest} disabled={saving || testing}>
              {testing ? "Testing…" : "Test Connection"}
            </MDButton>
            <Box sx={{ display: "flex", gap: 1 }}>
              <MDButton onClick={closeDialog} disabled={saving || testing}>
                Cancel
              </MDButton>
              <MDButton
                variant="contained"
                backgroundColor="#52009b"
                color="#52009b"
                sx={{ color: "#f7f4fb" }}
                onClick={handleSave}
                disabled={saving || testing}
              >
                {saving ? "Saving…" : editId ? "Update" : "Add to Library"}
              </MDButton>
            </Box>
          </DialogActions>
        </Dialog>

        {/* Delete confirm dialog */}
        <Dialog
          open={Boolean(deleteTarget)}
          onClose={() => setDeleteTarget(null)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Delete library entry?</DialogTitle>
          <DialogContent>
            <Typography>
              Delete <strong>{deleteTarget?.name}</strong>? This cannot be undone and will not
              affect signposts already linked to risk records.
            </Typography>
          </DialogContent>
          <DialogActions>
            <MDButton onClick={() => setDeleteTarget(null)}>Cancel</MDButton>
            <MDButton variant="contained" color="error" onClick={handleDelete}>
              Delete
            </MDButton>
          </DialogActions>
        </Dialog>

        {/* Snackbar feedback */}
        <Snackbar
          open={snack.open}
          autoHideDuration={3500}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
            {snack.msg}
          </Alert>
        </Snackbar>
      </Box>
    </DashboardLayout>
  );
}
